/**
 * Two hooks: `useQuery` and `useMutation`.
 *
 * Deliberately not a data-fetching library. The admin panel needs exactly four
 * things - load, reload, know it is loading, know it failed - and the whole of
 * that is below. A cache would also be actively wrong here: every screen in this
 * app exists to show the CURRENT state of a queue an admin is about to act on,
 * and a stale cache is the one failure mode that would make an admin act on a
 * decision someone else already made. So each screen loads when it mounts and
 * reloads when the admin says so (or after a mutation).
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type QueryState<T> = {
  data: T | null;
  error: unknown;
  /** True only for the FIRST load; a reload keeps the previous data visible. */
  loading: boolean;
  /** True for a reload while data is already on screen. */
  refreshing: boolean;
  reload: () => void;
};

/**
 * Run `load` on mount and whenever `deps` change.
 *
 * Out-of-order responses are discarded: a filter change fires a second request,
 * and without the generation counter a slow first response could land after the
 * faster second one and show the previous filter's rows under the new filter's
 * controls.
 */
export function useQuery<T>(load: () => Promise<T>, deps: unknown[]): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);

  // The loader closes over the current filters, so it is re-read each run rather
  // than being a dependency of the effect (which would re-run on every render,
  // since a new arrow function is a new identity).
  const loadRef = useRef(load);
  loadRef.current = load;

  const generation = useRef(0);
  const hasData = data !== null;

  useEffect(() => {
    const current = ++generation.current;
    if (hasData) setRefreshing(true);
    else setLoading(true);
    setError(null);

    loadRef
      .current()
      .then((result) => {
        if (current !== generation.current) return;
        setData(result);
      })
      .catch((caught: unknown) => {
        if (current !== generation.current) return;
        setError(caught);
      })
      .finally(() => {
        if (current !== generation.current) return;
        setLoading(false);
        setRefreshing(false);
      });
    // `hasData` is intentionally NOT a dependency: it only chooses which spinner
    // to show, and including it would re-fetch the moment the first response
    // arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, error, loading, refreshing, reload };
}

export type MutationState<A extends unknown[], T> = {
  /** Resolves to the result, or to `undefined` if it failed - the error is on
   *  `error`, so a caller that only wants the happy path can ignore the result. */
  run: (...args: A) => Promise<T | undefined>;
  pending: boolean;
  error: unknown;
  /** The result of the last successful run, for a confirmation panel. */
  result: T | null;
  reset: () => void;
};

/**
 * Run a mutation, tracking pending and error state.
 *
 * `onSuccess` is called with the result and is where a screen reloads its list
 * or closes its dialog. Note there is no optimistic update anywhere in this app:
 * every mutation here is an authorization decision by an admin about someone
 * else's account, listing or money, and showing it as done before the server
 * agreed would be a lie the admin might act on.
 */
export function useMutation<A extends unknown[], T>(
  action: (...args: A) => Promise<T>,
  options: { onSuccess?: (result: T) => void } = {},
): MutationState<A, T> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<T | null>(null);

  const actionRef = useRef(action);
  actionRef.current = action;
  const onSuccessRef = useRef(options.onSuccess);
  onSuccessRef.current = options.onSuccess;

  const run = useCallback(async (...args: A): Promise<T | undefined> => {
    setPending(true);
    setError(null);
    try {
      const value = await actionRef.current(...args);
      setResult(value);
      onSuccessRef.current?.(value);
      return value;
    } catch (caught: unknown) {
      setError(caught);
      return undefined;
    } finally {
      setPending(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { run, pending, error, result, reset };
}

/**
 * `window.confirm`, wrapped so a destructive action always asks.
 *
 * A native dialog rather than a styled modal on purpose: it cannot be dismissed
 * by a stray click outside it, it is focus-trapped by the browser, and it is one
 * line at each call site. The destructive actions in this app - suspending a
 * member, archiving a partner, deleting a category - are rare and irreversible
 * enough that a deliberate stop is the right friction.
 */
export function confirmAction(message: string): boolean {
  return window.confirm(message);
}
